import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { IJob, IJobForm } from 'core/interface/job';
import { CompetencieService } from 'services/competencies/competencies';
import useLocales from 'hooks/useLocales';
import Button from 'components/Button/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import NewJobPopup from 'components/shared/Popups/NewJobPopup/Index';
import CompetenciesSelect from 'components/shared/CompetenciesSelect';
import MatchingProfileBlock from 'components/MatchingProfileBlock/Index';
import NoData from 'components/shared/NoData/Index';
import { JobService } from 'services/jobs/job';
import { RootState } from '../../../redux/store';
import CompetenciesPopup from 'components/shared/Popups/CompetenciesPopup/Index';
import { getCompetenciesSelectFooter } from 'components/shared/CompetenciesSelect';
import Pagination from '@material-ui/core/Pagination';

import './Index.scss';

const PostAJob: FC = (): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [ids, setIds] = useState<string[]>([]);
  const { jobs } = useSelector((state: RootState) => state.jobs);
  const [showJobIndustriesPopup, setShowJobIndustriesPopup] =
    useState<boolean>(false);
  const [firstStepData, setFirstStepData] = useState<IJobForm | number[]>();
  const [selectedJobId, setSelectedJobId] = useState<any>();
  const [selectedJob, setSelectedJob] = useState<IJob | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [step, setStep] = useState(1);
  const [scores, setScores] = useState<{ [label: string]: number }>({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const { assessmentByGroup } = useSelector(
    (state: RootState) => state.assessment
  );

  const handleOpenPopup = (): void => {
    setShowPopup(true);
    setSelectedJobId('');
    setSelectedJob(null);
  };

  const getAssessment = () => {
    JobService.getAssessmentByGroup();
  };

  const getApplications = (): void => {
    setLoading(true);
    setTimeout(() => {
      JobService.getJobs({ limit, offset });
      setLoading(false);
    }, 1000);
  };

  const togglePopUp = (): void => {
    setShowPopup(!showPopup);
  };

  const toggleMatchPopUp = (): void => {
    setShowJobIndustriesPopup(!showJobIndustriesPopup);
    setStep(1);
  };

  useEffect(() => {
    getCompetencies();
    getApplications();
    getAssessment();
  }, [limit, offset]);

  useEffect(() => {
    const foundJob = jobs?.results?.find(
      (profile: IJob) => profile.id === selectedJobId
    );

    if (foundJob) {
      setSelectedJob(foundJob);
    }
  }, [jobs, selectedJobId]);

  useEffect(() => {
    if (jobs && jobs.count !== undefined) {
      setTotalCount(jobs.count);
    } else if (jobs && jobs.results) {
      setTotalCount(jobs.results.length);
    }
  }, [jobs]);

  useEffect(() => {
    setPage(1);
  }, [jobs]);

  const handleSetSelected = (label: string, id: any, text: string) => {
    const newSelected: string[] = [...selectedLabels];
    const newId: string[] = [...ids];
    const index = selectedLabels.indexOf(label);
    const indexID = ids.indexOf(id);
    if (index !== -1 && indexID !== -1) {
      newSelected.splice(index, 1);
      newId.splice(indexID, 1);
    } else if (newSelected.length < 10 && newId.length < 10) {
      newSelected.push(label);
      newId.push(id);
    } else if (newSelected.length < 7) {
      return;
    }
    setSelectedLabels(newSelected);
    setIds(newId);
  };

  const handleDelete = (id: number) => {
    JobService.deleteJobByIdOrSlug(id);
  };

  const handleNextStep = (evt?: IJobForm | number[]): void => {
    evt && setFirstStepData(evt);
    togglePopUp();
    setShowJobIndustriesPopup(!showJobIndustriesPopup);
  };

  const handleCompetenciesNextStep = () => {
    if (selectedLabels.length >= 7 && selectedLabels.length <= 10) {
      const idLabelArray = ids.map((id, index) => ({
        id,
        competency: selectedLabels[index]
      }));

      // console.log(selectedJob, selectedJob?.competency_min_scores, selectedJobId, idLabelArray, "score");
      const existingLabels = selectedJob?.competency_labels || [];
      const competencyScores = selectedJob?.competency_min_scores || [];
      const data = {
        firstStepData,
        competencies: idLabelArray,
        edit: selectedJobId ?? false,
        scores: idLabelArray.map(({ competency }) =>
          existingLabels.includes(competency)
            ? competencyScores[existingLabels.indexOf(competency)]
            : 0
        ),
        postAJob: true
      };
      localStorage.setItem('selected', JSON.stringify(data));
      navigate('/culture');
    }
  };

  const getCompetencies = (): void => {
    CompetencieService.getCompetencies();
  };

  const getJobApplication = (id: number) => {
    navigate(`/find-candidate/${id}`);
  };

  const getJobList = (id: number, type: string) => {
    navigate(`/match-result/${type}/${id}`);
  };

  useEffect(() => {
    if (selectedJob?.id) {
      setSelectedLabels(selectedJob?.competency_labels);
      const a = selectedJob?.competencies?.map((e: any) => e?.competency?.id);
      setIds(a);
    } else {
      setSelectedLabels([]);
      setIds([]);
    }
  }, [selectedJob]);

  const handleSavePopup = (
    selectedLabels: string[],
    scores: Record<string, number>,
    formValues: any
  ) => {
    const allCompetencies = assessmentByGroup.flatMap(
      (group: any) => group.competencies
    );
    const nameToId: Record<string, number> = {};
    allCompetencies.forEach((c: any) => {
      nameToId[c.name] = c.id;
    });

    // Helper to get value from formValues, then firstStepData, then fallback
    const getValue = (key: string, fallback: any = undefined) => {
      if (
        formValues &&
        formValues[key] !== undefined &&
        formValues[key] !== null
      )
        return formValues[key];
      if (
        firstStepData &&
        typeof firstStepData === 'object' &&
        !Array.isArray(firstStepData) &&
        (firstStepData as any)[key] !== undefined &&
        (firstStepData as any)[key] !== null
      )
        return (firstStepData as any)[key];
      return fallback;
    };

    // Ensure requirements is always an array
    let requirements = getValue('requirements', []);
    if (typeof requirements === 'string') {
      requirements = requirements ? [requirements] : [];
    }
    if (!Array.isArray(requirements) || requirements === null) {
      requirements = [];
    }

    // Ensure skills and industries are arrays of numbers
    let skills = getValue('skills', []);
    if (!Array.isArray(skills) || skills === null) skills = [];
    skills = skills.map((s: any) => (typeof s === 'number' ? s : s?.id ?? s));
    let industries = getValue('industries', []);
    if (!Array.isArray(industries) || industries === null) industries = [];
    industries = industries.map((i: any) =>
      typeof i === 'number' ? i : i?.id ?? i
    );

    // Format locations as an array of location objects with id and display_name
    let locations = getValue('locations', []);
    if (!Array.isArray(locations) || locations === null) locations = [];

    // Ensure each location has id and display_name
    locations = locations
      .map((loc: any) => {
        if (typeof loc === 'object' && loc !== null) {
          return {
            id: loc.id || -1,
            display_name: loc.display_name || ''
          };
        }
        return { id: -1, display_name: '' };
      })
      .filter((loc: any) => loc.id !== -1);

    // Ensure culture_profile is an object with id, title, is_culture
    let culture_profile = getValue('culture_profile', {
      id: -1,
      title: '',
      is_culture: false
    });
    if (typeof culture_profile !== 'object' || culture_profile === null)
      culture_profile = { id: -1, title: '', is_culture: false };
    if (culture_profile.id === undefined) culture_profile.id = -1;
    if (culture_profile.title === undefined) culture_profile.title = '';
    if (culture_profile.is_culture === undefined)
      culture_profile.is_culture = false;

    // Compose the request body with all required fields
    // Note: Some field names are different between the form and the API:
    // - 'requirements' in the form becomes 'musts' for the API
    // - 'required_experience' in the form becomes 'work_exp' for the API
    const reqBody = {
      title: getValue('job_title', ''),
      description: getValue('job_description', ''),
      is_public: Boolean(getValue('is_public', false)),
      degree: Number(getValue('degree', 0)),
      locations,
      industries,
      musts: requirements, // Form field: requirements -> API field: musts
      skills,
      work_exp: Number(getValue('required_experience', 0)), // Form field: required_experience -> API field: work_exp
      prefered_salary: Number(getValue('prefered_salary', 0)),
      competencies: selectedLabels.map((label: string) => ({
        competency: nameToId[label],
        min_score: scores[label] ?? 5
      })),
      culture_profile: culture_profile.id ?? null,
      if_office_work: Boolean(getValue('if_office_work', false)),
      is_remote: Boolean(getValue('is_remote', false)),
      status: String(getValue('status', '')),
      contract: Number(getValue('contract_type', 0)),
      compensation: Number(getValue('compensation', 0))
    };

    if (isEditing && selectedJobId) {
      JobService.saveJobEdit((firstStepData as any).edit, reqBody);
      setIsEditing(false);
    } else {
      JobService.saveJob(reqBody);
    }
    getApplications();
    setShowJobIndustriesPopup(false);
    setStep(1);
  };

  return (
    <div className="postjob">
      <div className="postjob__header">
        <span className="postjob__header-title">{t('Job Posts')}</span>
        <Button
          onClick={handleOpenPopup}
          t={t}
          title={t('Create New Job Post')}
          className="postjob__header-btn"
        />
      </div>
      {loading ? (
        <div className="loadingContainer">
          <CircularProgress className="loading" />
        </div>
      ) : jobs?.results?.length === 0 ? (
        <NoData />
      ) : (
        <>
          <div className="postjob_blocks">
            {jobs?.results?.map((el: any) => (
              <MatchingProfileBlock
                id={el.id}
                title={el.title}
                name={''}
                date={el.last_competencies_updated_on}
                key={el.id}
                setEditPopUp={() => {
                  setIsEditing(true);
                  togglePopUp();
                }}
                setDeletedItem={handleDelete}
                setSelectedMatchProfileId={setSelectedJobId}
                getOnePost={getJobApplication}
                getList={getJobList}
                type="job-posts"
                createdBy={el?.user || ''}
              />
            ))}
          </div>
          <div
            style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}
          >
            <Pagination
              className="pagination"
              count={Math.ceil((jobs?.count || 0) / limit)}
              page={page}
              onChange={(_, value) => setPage(value)}
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </div>
        </>
      )}

      {showPopup && (
        <Popup
          title={t(`${isEditing ? 'Edit your job post' : 'Create your new job post'} `)}
          description="Please let people know what's important for your job opening"
          onClose={togglePopUp}
        >
          <NewJobPopup nextStep={handleNextStep} selectedJob={selectedJob} />
        </Popup>
      )}
      {showJobIndustriesPopup && (
        <CompetenciesPopup
          className="popup-add-new"
          title={t('Create your new job post')}
          description="Please select the most important competencies for your job opening."
          onClose={toggleMatchPopUp}
          footer={getCompetenciesSelectFooter({
            step,
            t,
            handleBack: () => setStep(1),
            handleSave: () =>
              handleSavePopup(selectedLabels, scores, firstStepData),
            handleNext: () => setStep(2),
            selectedList: selectedLabels,
            disabled: selectedLabels.length < 7 || selectedLabels.length > 10
          })}
        >
          <CompetenciesSelect
            step={step}
            setStep={setStep}
            competencies={assessmentByGroup}
            selectedList={selectedLabels}
            onSelect={handleSetSelected}
            matchProfile={false}
            scores={scores}
            setScores={setScores}
          />
        </CompetenciesPopup>
      )}
    </div>
  );
};

export default PostAJob;
